import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5L11.5 4.5L4.5 11.5H2.5V9.5L9.5 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 4L10 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SkeletonRow = () => (
  <tr>
    {[1,2,3,4,5]?.map(i => (
      <td key={i} className="py-3 px-3">
        <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: i === 1 ? 40 : 60 }} />
      </td>
    ))}
  </tr>
);

const ActivenessSection = ({ servers, showToast }) => {
  const { t } = useTranslation();
  const [activeServer, setActiveServer] = useState('');
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (servers?.length > 0 && !activeServer) {
      setActiveServer(servers?.[0]?.id);
    }
  }, [servers, activeServer]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase?.from('activeness_config')?.select('*')?.order('level');
    if (error) {
      showToast(t('admin.config.errorLoadingActiveness') + ': ' + error?.message, 'error');
    } else {
      setAllData(data || []);
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = allData?.filter(r => r?.server === activeServer);
  const color = servers?.find(s => s?.id === activeServer)?.color || '#888888';

  const handleEditClick = (row) => {
    setEditingId(row?.id);
    setEditValues({
      min_live_days: String(row?.min_live_days),
      min_live_hours: String(row?.min_live_hours),
      min_diamonds: String(row?.min_diamonds ?? 0),
      bonus_ratio: String(row?.bonus_ratio),
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (rowId) => {
    setSaving(true);
    const { error } = await supabase?.from('activeness_config')?.update({
        min_live_days: parseInt(editValues?.min_live_days) || 0,
        min_live_hours: parseFloat(editValues?.min_live_hours) || 0,
        min_diamonds: parseInt(editValues?.min_diamonds) || 0,
        bonus_ratio: parseFloat(editValues?.bonus_ratio) || 0,
      })?.eq('id', rowId);
    setSaving(false);
    if (error) {
      showToast(error?.message, 'error');
    } else {
      showToast(t('admin.config.rowUpdated'), 'success');
      setEditingId(null);
      setEditValues({});
      fetchData();
    }
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
        <h2 className="text-white font-semibold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>
          {t('admin.config.activenessTitle')}
        </h2>
        <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
          {t('admin.config.activenessSubtitle')}
        </p>
      </div>
      {/* Server Tabs */}
      <div className="flex gap-1 px-6 pt-4 pb-2 flex-wrap">
        {servers?.map(server => {
          const isActive = activeServer === server?.id;
          const c = server?.color || '#888888';
          return (
            <button
              key={server?.id}
              onClick={() => setActiveServer(server?.id)}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: isActive ? `${c}22` : 'transparent',
                color: isActive ? c : 'rgba(255,255,255,0.35)',
                border: `1px solid ${isActive ? c : '#1f1f1f'}`,
              }}
            >
              {server?.id}
            </button>
          );
        })}
      </div>
      {/* Table */}
      <div className="px-6 pb-6 pt-2 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              {[t('admin.config.colLevel'), t('admin.config.colMinDays'), t('admin.config.colMinHours'), t('admin.config.colMinDiamonds'), t('admin.config.colBonusRatio'), '']?.map(col => (
                <th
                  key={col}
                  className="text-left py-2.5 px-3 text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5]?.map(i => <SkeletonRow key={i} />)
            ) : rows?.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.config.noDataServer')}
                </td>
              </tr>
            ) : (
              rows?.map((row, i) => {
                const isEditing = editingId === row?.id;
                return (
                  <tr
                    key={row?.id}
                    style={{ borderBottom: i < rows?.length - 1 ? '1px solid #1a1a1a' : 'none' }}
                  >
                    {/* Level */}
                    <td className="py-3 px-3">
                      <span
                        className="inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold"
                        style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}
                      >
                        L{row?.level}
                      </span>
                    </td>
                    {/* Min Jours */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues?.min_live_days}
                          onChange={e => setEditValues(v => ({ ...v, min_live_days: e?.target?.value }))}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-16"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        <span className="text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {row?.min_live_days} j
                        </span>
                      )}
                    </td>
                    {/* Min Heures */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues?.min_live_hours}
                          onChange={e => setEditValues(v => ({ ...v, min_live_hours: e?.target?.value }))}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-16"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        <span className="text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {row?.min_live_hours}h
                        </span>
                      )}
                    </td>
                    {/* Min Diamonds */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues?.min_diamonds}
                          onChange={e => setEditValues(v => ({ ...v, min_diamonds: e?.target?.value }))}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-20"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        <span className="text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                          💎 {(row?.min_diamonds ?? 0)?.toLocaleString('fr-FR')}
                        </span>
                      )}
                    </td>
                    {/* Bonus % */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues?.bonus_ratio}
                          onChange={e => setEditValues(v => ({ ...v, bonus_ratio: e?.target?.value }))}
                          className="rounded px-2 py-1 text-base text-white outline-none border w-16"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        <span className="text-sm font-semibold" style={{ color, fontFamily: 'Inter, sans-serif' }}>
                          {row?.bonus_ratio}%
                        </span>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleSave(row?.id)}
                            disabled={saving}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
                            style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', opacity: saving ? 0.6 : 1 }}
                          >
                            <CheckIcon /> {t('admin.config.saveShort')}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs"
                            style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.4)' }}
                          >
                            <XIcon />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(row)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                          style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30` }}
                        >
                          <PencilIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivenessSection;
